import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import ProducerPageView from '../components/producerPage/ProducerPageView.jsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Public Producer Validation Page at /p/:slug (no login). Only published pages resolve.
 * @returns {JSX.Element}
 */
export default function PublicProducerPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_BASE}/producer-page/public/${encodeURIComponent(slug)}`)
      .then((r) => r.json())
      .then((d) => { if (alive) { if (d.ok) setPage(d.page); else setError(d.error || 'Page not found'); } })
      .catch(() => { if (alive) setError('Could not load this page.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (page?.title) document.title = page.title;
  }, [page]);

  const model = useMemo(() => page && ({
    ...page,
    images: {
      profile: `${API_BASE}/producer-page/public/${encodeURIComponent(slug)}/profile`,
      background: `${API_BASE}/producer-page/public/${encodeURIComponent(slug)}/background`,
    },
  }), [page, slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-slate-500">
        <RefreshCw className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-rose-500" />
          <h1 className="mt-3 text-lg font-bold text-slate-900">Page not found</h1>
          <p className="mt-1 text-sm text-slate-500">{error || 'This producer page does not exist or is not published.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-4xl">
        <ProducerPageView model={model} slug={slug} />
      </main>
    </div>
  );
}
